import { useState } from "react";
import { useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  generatePath,
} from "react-router-dom";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SidebarIcons from "./SidebarIcons";
import Footer from "./Footer";
import BottomBarIcons from "./BottomBarIcons";
import Toaster from "./common/Toaster";
import LoadingComponent from "./common/LoadingComponent";
import http from "../api/server";
import { TRIGGER_EMAIL_TO_SEND_BOOKING_LINK } from "../api/apiPath";
import { Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
const SendBookingLink = () => {
  const { clientNameParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userObj } = useSelector((state) => state.user);

  const selectedIds = location.state?.selectedIds || [];

  const [hideSidebar, setHideSidebar] = useState(false);

  const [bookingFormData, setBookingFormData] = useState({
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
  });
  const [bookingErrorData, setBookingErrorData] = useState({
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
  });

  // Per-day slots for booking window: { [date: 'YYYY-MM-DD']: { startTime: 'HH:MM', endTime: 'HH:MM' } }
  const [bookingSlotsByDate, setBookingSlotsByDate] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [toastData, setToastData] = useState({
    toastShow: false,
    toastType: "",
    toastString: "",
  });

  const closeButton = () => {
    setToastData({
      toastShow: false,
      toastType: "",
      toastString: "",
    });
  };

  // const today = new Date().toISOString().split("T")[0];

  const handleBookingDataChange = (field, e) => {
    const value = e.target.value;
    setBookingFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    setBookingErrorData((prevErrors) => ({
      ...prevErrors,
      [field]: value ? "" : prevErrors[field],
    }));
    // When only main start changes: update slot start for all dates, keep each slot's end
    if (field === "startTime") {
      setBookingSlotsByDate((prev) => {
        const dates = generateDateRange(
          bookingFormData.startDate,
          bookingFormData.endDate,
        );
        const mainEnd =
          bookingFormData.endTime && bookingFormData.endTime > SLOT_MAX_TIME
            ? SLOT_MAX_TIME
            : bookingFormData.endTime || "";
        const next = { ...prev };
        dates.forEach((d) => {
          next[d] = {
            startTime: value,
            endTime: prev[d]?.endTime || mainEnd,
          };
        });
        return next;
      });
    }
    // When only main end changes: update slot end for all dates, keep each slot's start
    if (field === "endTime") {
      const cappedEnd =
        value && value > SLOT_MAX_TIME ? SLOT_MAX_TIME : value || "";
      setBookingSlotsByDate((prev) => {
        const dates = generateDateRange(
          bookingFormData.startDate,
          bookingFormData.endDate,
        );
        const mainStart = bookingFormData.startTime || "";
        const next = { ...prev };
        dates.forEach((d) => {
          next[d] = {
            startTime: prev[d]?.startTime || mainStart,
            endTime: cappedEnd,
          };
        });
        return next;
      });
    }
  };

  function convertToISOTimeFormat(time) {
    const [hh, mm] = time.split(":");
    const ss = "00";
    const mmm = "000";

    return `${hh}:${mm}:${ss}.${mmm}`;
  }

  // Generate an inclusive list of dates (YYYY-MM-DD) between startDate and endDate
  const generateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      start > end
    ) {
      return [];
    }
    const dates = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const handleSlotChange = (date, field, value) => {
    setBookingSlotsByDate((prev) => ({
      ...prev,
      [date]: {
        ...(prev?.[date] || {}),
        [field]: value,
      },
    }));
  };

  // Effective slot for a date: per-day override or main start/end time (end time capped at 21:00)
  const getEffectiveSlot = (date) => {
    const override = bookingSlotsByDate[date];
    if (override?.startTime && override?.endTime) {
      return { startTime: override.startTime, endTime: override.endTime };
    }
    const mainStart = bookingFormData.startTime || "";
    let mainEnd = bookingFormData.endTime || "";
    // Cap end time at 21:00 if user selected more than that
    if (mainEnd && mainEnd > SLOT_MAX_TIME) {
      mainEnd = SLOT_MAX_TIME;
    }
    return {
      startTime: mainStart,
      endTime: mainEnd,
    };
  };

  // Allowed slot window: 8 AM to 11 PM (23:00)
  const SLOT_MIN_TIME = "08:00";
  const SLOT_MAX_TIME = "23:00";
  // Booking window end time max 9 PM (21:00)
  const BOOKING_END_MAX_TIME = "21:00";

  // Time options at 15-minute intervals from 08:00 to 23:00 (used for main start and slots)
  const getTimeOptions15Min = () => {
    const options = [];
    for (let h = 8; h <= 23; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (h === 23 && m > 0) break;
        const HH = String(h).padStart(2, "0");
        const MM = String(m).padStart(2, "0");
        options.push(`${HH}:${MM}`);
      }
    }
    return options;
  };

  // Booking window end time only: 08:00 to 21:00
  const getBookingEndTimeOptions = () => {
    const options = [];
    for (let h = 8; h <= 21; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (h === 21 && m > 0) break;
        const HH = String(h).padStart(2, "0");
        const MM = String(m).padStart(2, "0");
        options.push(`${HH}:${MM}`);
      }
    }
    return options;
  };

  const timeOptions15Min = getTimeOptions15Min();
  const bookingEndTimeOptions = getBookingEndTimeOptions();

  // Generate time options in 15-min intervals between startTime and endTime
  const generateTimeOptionsInRange = (startTime, endTime) => {
    if (!startTime || !endTime) return timeOptions15Min;

    const start = startTime < SLOT_MIN_TIME ? SLOT_MIN_TIME : startTime;
    const end = endTime > SLOT_MAX_TIME ? SLOT_MAX_TIME : endTime;

    if (start > end) return [];

    const options = [];
    const [startH, startM] = start.split(":").map(Number);
    const [endH, endM] = end.split(":").map(Number);

    let currentH = startH;
    let currentM = startM;

    while (currentH < endH || (currentH === endH && currentM <= endM)) {
      const HH = String(currentH).padStart(2, "0");
      const MM = String(currentM).padStart(2, "0");
      options.push(`${HH}:${MM}`);

      currentM += 15;
      if (currentM >= 60) {
        currentM = 0;
        currentH += 1;
      }

      if (currentH > endH || (currentH === endH && currentM > endM)) break;
    }

    return options;
  };

  // Options for start time dropdown: from max(08:00, main startTime) to main endTime
  const getStartTimeOptionsForSlot = (currentValue) => {
    const mainStart = bookingFormData.startTime || SLOT_MIN_TIME;
    const mainEnd = bookingFormData.endTime || SLOT_MAX_TIME;
    const effectiveStart =
      mainStart < SLOT_MIN_TIME ? SLOT_MIN_TIME : mainStart;
    const effectiveEnd = mainEnd > SLOT_MAX_TIME ? SLOT_MAX_TIME : mainEnd;

    const options = generateTimeOptionsInRange(effectiveStart, effectiveEnd);

    // Include current value if it's not in the list but within allowed range
    if (currentValue && !options.includes(currentValue)) {
      if (currentValue >= SLOT_MIN_TIME && currentValue <= SLOT_MAX_TIME) {
        return [currentValue, ...options].sort();
      }
    }

    return options;
  };

  // Options for end time dropdown: from 15 min after selected start time to main endTime (capped at 21:00)
  const getEndTimeOptionsForSlot = (selectedStartTime, currentValue) => {
    // Get main end time, capped at 21:00
    const mainEnd = bookingFormData.endTime || SLOT_MAX_TIME;
    const effectiveEnd = mainEnd > SLOT_MAX_TIME ? SLOT_MAX_TIME : mainEnd;

    // Determine the minimum start time for end options (must be after selected start)
    let minStartTime = SLOT_MIN_TIME;
    if (selectedStartTime) {
      if (selectedStartTime < SLOT_MIN_TIME) {
        minStartTime = SLOT_MIN_TIME;
      } else {
        minStartTime = selectedStartTime;
      }
    }

    // For end time, start from 15 minutes after the selected start time (to ensure end > start)
    const [startH, startM] = minStartTime.split(":").map(Number);
    let adjustedStartH = startH;
    let adjustedStartM = startM + 15;
    if (adjustedStartM >= 60) {
      adjustedStartM = 0;
      adjustedStartH += 1;
    }
    const adjustedStart = `${String(adjustedStartH).padStart(2, "0")}:${String(adjustedStartM).padStart(2, "0")}`;

    // If adjusted start exceeds the effective end, return options up to effective end only
    if (adjustedStart > effectiveEnd) {
      // Still allow the effective end if it's valid
      const options = [];
      if (effectiveEnd >= adjustedStart || effectiveEnd >= minStartTime) {
        options.push(effectiveEnd);
      }
      if (
        currentValue &&
        currentValue <= effectiveEnd &&
        currentValue >= minStartTime &&
        currentValue <= SLOT_MAX_TIME
      ) {
        if (!options.includes(currentValue)) {
          options.push(currentValue);
        }
      }
      return options.sort();
    }

    const options = generateTimeOptionsInRange(adjustedStart, effectiveEnd);

    // Include current value if it's not in the list but within allowed range and after start
    if (currentValue && !options.includes(currentValue)) {
      if (
        currentValue >= minStartTime &&
        currentValue <= effectiveEnd &&
        currentValue <= SLOT_MAX_TIME
      ) {
        return [currentValue, ...options].sort();
      }
    }

    return options;
  };

  const validate = () => {
    const newErrors = {};

    if (!bookingFormData.startDate)
      newErrors.startDate = "Start Date is required";
    if (!bookingFormData.endDate) newErrors.endDate = "End Date is required";
    if (!bookingFormData.startTime)
      newErrors.startTime = "Start Time is required";
    if (!bookingFormData.endTime) newErrors.endTime = "End Time is required";
    if (
      bookingFormData.startTime &&
      (bookingFormData.startTime < SLOT_MIN_TIME ||
        bookingFormData.startTime > SLOT_MAX_TIME)
    ) {
      newErrors.startTime = "Start time must be between 8:00 AM and 11:00 PM";
    }
    if (
      bookingFormData.endTime &&
      (bookingFormData.endTime < SLOT_MIN_TIME ||
        bookingFormData.endTime > BOOKING_END_MAX_TIME)
    ) {
      newErrors.endTime = "End time must be between 8:00 AM and 9:00 PM";
    }
    if (
      bookingFormData.startTime &&
      bookingFormData.endTime &&
      bookingFormData.startTime >= bookingFormData.endTime
    ) {
      newErrors.endTime = "End time must be after start time";
    }

    return newErrors;
  };

  const renderPerDaySlots = () => {
    const { startDate, endDate, startTime, endTime } = bookingFormData;
    // Show slots only after user has selected both dates and times
    if (!startDate || !endDate || !startTime || !endTime) return null;

    const dates = generateDateRange(startDate, endDate);
    if (!dates || dates.length === 0) return null;

    return (
      <>
        <p className="small mb-2">
          Set start and end time for each day below. By default all days use the
          times above.
        </p>
        {/* Slot list - revert UI: remove border/background from container and row, revert label/date styles */}
        <div
          className="day-slots-list d-flex flex-column gap-2"
          style={{
            maxHeight: "320px",
            overflowY: "auto",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "0.5rem",
            backgroundColor: "#f8fafc",
          }}
        >
          {dates.map((date, index) => {
            const displayDate = new Date(date).toLocaleDateString("en-GB", {
              weekday: "short",
              day: "numeric",
              month: "short",
              year: "numeric",
            });
            const effective = getEffectiveSlot(date);
            const isEven = index % 2 === 0;

            return (
              <div
                key={date}
                className="schedule-date-box-extended d-flex align-items-start gap-3"
                style={{
                  padding: "0.75rem 1rem",
                  backgroundColor: isEven ? "#ffffff" : "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  borderLeft: "3px solid #081634",
                }}
              >
                <div className="form-group mb-0" style={{ minWidth: "120px" }}>
                  <label
                    className="mb-0"
                    style={{
                      fontSize: "11px",
                      fontWeight: 500,
                      color: "#64748b",
                    }}
                  >
                    Date
                  </label>
                  <div
                    className="mt-1"
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#0f172a",
                      lineHeight: "28px",
                    }}
                  >
                    {displayDate}
                  </div>
                </div>
                <div className="d-flex gap-2 align-items-start flex-wrap flex-grow-1">
                  <div
                    className="form-group mb-0"
                    style={{ minWidth: "80px", maxWidth: "100px" }}
                  >
                    <label
                      className="mb-0"
                      style={{
                        fontSize: "11px",
                        fontWeight: 500,
                        color: "#64748b",
                      }}
                    >
                      Start
                    </label>
                    <select
                      className="form-control form-control-sm mt-1"
                      style={{
                        fontSize: "12px",
                        padding: "0.2rem 0.35rem",
                        height: "28px",
                        borderRadius: "6px",
                      }}
                      value={effective.startTime}
                      onChange={(e) =>
                        handleSlotChange(date, "startTime", e.target.value)
                      }
                    >
                      {getStartTimeOptionsForSlot(effective.startTime).map(
                        (t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                  <div
                    className="form-group mb-0"
                    style={{ minWidth: "80px", maxWidth: "100px" }}
                  >
                    <label
                      className="mb-0"
                      style={{
                        fontSize: "11px",
                        fontWeight: 500,
                        color: "#64748b",
                      }}
                    >
                      End
                    </label>
                    <select
                      className="form-control form-control-sm mt-1"
                      style={{
                        fontSize: "12px",
                        padding: "0.2rem 0.35rem",
                        height: "28px",
                        borderRadius: "6px",
                      }}
                      value={effective.endTime}
                      onChange={(e) =>
                        handleSlotChange(date, "endTime", e.target.value)
                      }
                    >
                      {getEndTimeOptionsForSlot(
                        effective.startTime,
                        effective.endTime,
                      ).map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const handleCancel = () => {
    navigate(
      generatePath("/client/:clientNameParam/leadDashboard", {
        clientNameParam,
      }),
    );
  };

  const handleSend = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setBookingErrorData(validationErrors);
      return;
    }
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      setToastData({
        toastShow: true,
        toastType: "failure",
        toastString: "No residents selected for booking email.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      let payload = {
        rmsIds: selectedIds,
        loggedInUserEmail: userObj?.userEmail,
        propertyName: "",
        startDate: bookingFormData.startDate,
        endDate: bookingFormData.endDate,
        startTime: convertToISOTimeFormat(bookingFormData.startTime) || "",
        endTime: convertToISOTimeFormat(bookingFormData.endTime) || "",
      };

      const dates = generateDateRange(
        bookingFormData.startDate,
        bookingFormData.endDate,
      );
      let slots = [];

      if (
        dates.length > 0 &&
        bookingFormData.startTime &&
        bookingFormData.endTime
      ) {
        // Use effective slot per date (per-day override or main start/end)
        slots = dates
          .map((date) => {
            const effective = getEffectiveSlot(date);
            if (!effective.startTime || !effective.endTime) return null;
            return {
              date,
              startTime: convertToISOTimeFormat(effective.startTime),
              endTime: convertToISOTimeFormat(effective.endTime),
            };
          })
          .filter(Boolean);
      }

      if (slots.length > 0) {
        payload.slots = slots;
        payload.slotsByDay = JSON.stringify(slots);
      }

      const response = await http.post(
        TRIGGER_EMAIL_TO_SEND_BOOKING_LINK,
        payload,
      );

      const errmsg = response?.data?.message || "Link sent successfully";

      if (!response.status || response.status >= 400) {
        setToastData({
          toastShow: true,
          toastType: "failure",
          toastString: errmsg,
        });
      } else {
        setToastData({
          toastShow: true,
          toastType: "success",
          toastString: errmsg,
        });
        // Navigate back after a short delay
        setTimeout(() => {
          handleCancel();
        }, 1500);
      }
    } catch (error) {
      console.error("Error triggering booking email:", error);
      const errmsg =
        error?.data?.message || "Error in sending link, please try again later";
      setToastData({
        toastShow: true,
        toastType: "failure",
        toastString: errmsg,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header hideSidebar={hideSidebar} setHideSidebar={setHideSidebar} />
      <div className="dashboard_body_main d-flex vh-100">
        {hideSidebar ? (
          <Sidebar setHideSidebar={setHideSidebar} hideSidebar={hideSidebar} />
        ) : (
          <SidebarIcons
            setHideSidebar={setHideSidebar}
            hideSidebar={hideSidebar}
          />
        )}

        <div className="d-flex dashboard_body flex-column py-1 w-100">
          <div className="p-2 border-0 w-100">
            <div className="dashboard_header">
              <h2 className="mb-0">Send Booking Link</h2>
              <p className="header-description">
                Select date range and time slots for the booking link email.
              </p>
            </div>

            {selectedIds.length === 0 && (
              <p className="text-danger small mb-3">
                No residents selected. Please go back to the Lead Dashboard and
                select at least one resident.
              </p>
            )}

            <h4 className="schedule-grid p-0 mb-2 mt-3 text-start">
              Booking Window
            </h4>
            <div className="resident-card px-2 pt-3 pb-4 mb-4">
              <div className="card-body-section px-3">
                <div className="form-group">
                  <label htmlFor="startDate">
                    Start date <span style={{ color: "red" }}>*</span>
                  </label>
                  {/* <input
                    className={
                      bookingErrorData.startDate
                        ? "input-error form-control"
                        : "form-control"
                    }
                    type="date"
                    id="startDate"
                    min={today}
                    required
                    placeholder="Start Date"
                    value={bookingFormData.startDate}
                    onChange={(e) => {
                      handleBookingDataChange("startDate", e);
                    }}
                  /> */}

                  <DatePicker
                    selected={
                      bookingFormData.startDate
                        ? new Date(bookingFormData.startDate)
                        : null
                    }
                    onChange={(date) =>
                      handleBookingDataChange("startDate", {
                        target: {
                          // value: date ? date.toISOString().split("T")[0] : "",
                          value: date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
        : "",
                        },
                      })
                    }
                    dateFormat="MM-dd-yyyy"
                    minDate={new Date()}
                    placeholderText="Start Date"
                    className={
                      bookingErrorData.startDate
                        ? "input-error form-control"
                        : "form-control"
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="endDate">
                    End date <span style={{ color: "red" }}>*</span>
                  </label>
                  {/* <input
                    className={
                      bookingErrorData.endDate
                        ? "input-error form-control"
                        : "form-control"
                    }
                    type="date"
                    id="endDate"
                    required
                    min={today}
                    placeholder="End Date"
                    value={bookingFormData.endDate}
                    onChange={(e) => {
                      handleBookingDataChange("endDate", e);
                    }}
                  /> */}
                  <DatePicker
                    selected={
                      bookingFormData.endDate
                        ? new Date(bookingFormData.endDate)
                        : null
                    }
                    onChange={(date) =>
                      handleBookingDataChange("endDate", {
                        target: {
                          // value: date ? date.toISOString().split("T")[0] : "",
                          value: date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
        : "",
                        },
                      })
                    }
                    dateFormat="MM-dd-yyyy"
                    minDate={
                      bookingFormData.startDate
                        ? new Date(bookingFormData.startDate)
                        : new Date()
                    }
                    placeholderText="End Date"
                    className={
                      bookingErrorData.endDate
                        ? "input-error form-control"
                        : "form-control"
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="startTime">
                    Start time <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    className={
                      bookingErrorData.startTime
                        ? "input-error form-control"
                        : "form-control"
                    }
                    id="startTime"
                    required
                    value={bookingFormData.startTime}
                    onChange={(e) => {
                      handleBookingDataChange("startTime", e);
                    }}
                  >
                    <option value="">Select start time</option>
                    {timeOptions15Min.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="endTime">
                    End time <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    className={
                      bookingErrorData.endTime
                        ? "input-error form-control"
                        : "form-control"
                    }
                    id="endTime"
                    required
                    value={bookingFormData.endTime}
                    onChange={(e) => {
                      handleBookingDataChange("endTime", e);
                    }}
                  >
                    <option value="">Select end time (up to 21:00)</option>
                    {bookingEndTimeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {(() => {
              const daySlotsContent = renderPerDaySlots();
              if (!daySlotsContent) return null;
              return (
                <div className="schedule-inspectors">
                  <p>Slots by day</p>
                  <div className="resident-card mb-4">{daySlotsContent}</div>
                </div>
              );
            })()}

            <div className="pb-3 button-group">
              <Button
                type="button"
                className="btn btn-tertiary"
                size="lg"
                variant="tertiary"
                onClick={handleCancel}
                style={{ width: "15%" }}
              >
                Cancel
              </Button>
              <Button
                // variant="primary"
                size="lg"
                className=" btn-primary"
                active
                type="button"
                onClick={handleSend}
                disabled={selectedIds.length === 0 || isSubmitting}
                style={{ width: "15%" }}
              >
                {isSubmitting ? "Sending..." : "Send"}
              </Button>
            </div>
          </div>

          <Toaster
            show={toastData.toastShow}
            toastType={toastData.toastType}
            title={toastData.toastString}
            theme={"dark"}
            position="top-center"
            closeButton={closeButton}
          />
          <LoadingComponent loading={isSubmitting} />
          <div className="bottom-bar-spacing"></div>
          <BottomBarIcons />
        </div>
        <Footer />
      </div>
    </>
  );
};

export default SendBookingLink;
